import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { GET_PRODUCT_SLIDER } from '../../queries/misc';
import useHydrationDelay from "../../hooks/useHydrationDelay";
import SlickSlider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ProductItem from '../../components/Category/ProductItem/ProductItem';
import SliderLoadingPlaceholder from './SliderLoadingPlaceholder';
import { normalizeProductItems } from '../../components/Category/Products/Products';
import './ProductSlider.scss';

const sliderSettings = {
  dots: false,
  arrows: true,
  infinite: true,
  speed: 700,
  centerPadding: '1px',
  className: 'center product-slider',
  adaptiveHeight: true,
  slidesToShow: 4,
  slidesToScroll: 4,
  responsive: [
    { breakpoint: 1225, settings: { slidesToShow: 4, slidesToScroll: 4 } },
    { breakpoint: 1100, settings: { arrows: true, dots: true, slidesToShow: 3, slidesToScroll: 3 } },
    { breakpoint: 918, settings: { arrows: true, dots: true, slidesToShow: 2, slidesToScroll: 2 } },
    { breakpoint: 550, settings: { slidesToShow: 1, slidesToScroll: 1 } },
  ],
};

// Definicja extractItems
const extractItems = (text) => {
  const regex = /(\S+)=["']?((?:.(?!["']?\s+(?:\S+)=|\s*\/?[>"']))+.)["']?/g;
  return text?.match(regex) || [];
};

// Definicja getCategoryId
const getCategoryId = (props) => {
  if (props?.conditionOption === 'category_ids' && props.categoryIds) {
    const numberPattern = /\d+/g;
    const matches = props.categoryIds.match(numberPattern);
    return matches?.length ? Number.parseInt(matches[0]) : undefined;
  }
  return undefined;
};

const NewProductSlider = ({
                            conditionsEncoded,
                            html,
                            sortOrder,
                            conditionOption,
                            categoryIds,
                            minHeight,
                            autoplay,
                            autoplaySpeed,
                            fade,
                            infinite,
                            showArrows,
                            showDots,
                            textAlign,
                            border,
                            borderColor,
                            borderWidth,
                            borderRadius,
                            marginTop,
                            marginRight,
                            marginBottom,
                            marginLeft,
                            paddingTop,
                            paddingRight,
                            paddingBottom,
                            paddingLeft,
                            cssClasses = [],
                          }) => {
  // Wszystkie hooki muszą być wywołane przed warunkowymi zwrotami
  const [products, setProducts] = useState([]);
  const isHydrated = useHydrationDelay();

  const extHtml = extractItems(html);
  const isCount = extHtml.find((x) => x.includes('products_count'));
  const productCount = isCount ? parseInt(isCount.match(/\d+/)[0], 10) : 5;

  const dynamicStyles = {
    minHeight,
    textAlign,
    borderRadius,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
  };

  const sliderOverrides = {
    autoplay: autoplay ?? sliderSettings.autoplay,
    autoplaySpeed: autoplaySpeed ?? sliderSettings.autoplaySpeed,
    fade: fade ?? sliderSettings.fade,
    infinite: infinite ?? sliderSettings.infinite,
    arrows: showArrows ?? sliderSettings.arrows,
    dots: showDots ?? sliderSettings.dots,
  };

  const { data, loading, error } = useQuery(GET_PRODUCT_SLIDER, {
    variables: {
      sort: sortOrder || undefined,
      category: getCategoryId({ conditionOption, categoryIds }),
      condition: conditionsEncoded,
      pageSize: productCount,
    },
    fetchPolicy: 'cache-first',
    skip: !isHydrated || !conditionsEncoded, // Nie wykonuj query podczas hydracji
  });

  useEffect(() => {
    if (data?.productsByCondition?.items && isHydrated) {
      try {
        const normalizedProducts = normalizeProductItems(data.productsByCondition.items);
        setProducts(normalizedProducts);
      } catch (err) {
        console.error('Error normalizing products:', err);
      }
    }
  }, [data, isHydrated]);

  // Nie renderuj podczas hydracji
  if (!isHydrated) {
    return null;
  }

  if (!html || !conditionsEncoded) return null;
  if (loading) return <SliderLoadingPlaceholder />;
  if (error || !data?.productsByCondition?.items?.length || products.length === 0) return null;

  const slideCount = Math.min(products.length, sliderSettings.slidesToShow);
  const adjustedSettings = {
    ...sliderSettings,
    ...sliderOverrides,
    slidesToShow: slideCount || 1,
    slidesToScroll: slideCount || 1,
    infinite: products.length > 1,
  };

  return (
      <ul>
        <SlickSlider
            {...adjustedSettings}
            style={dynamicStyles}
            className={['product-slider', ...cssClasses].join(' ')}
        >
          {products.map((item) => (
              <div key={item.sku} className="product-slide-wrapper">
                <ProductItem product={item} isCms />
              </div>
          ))}
        </SlickSlider>
      </ul>
  );
};

NewProductSlider.propTypes = {
  conditionsEncoded: PropTypes.string.isRequired,
  html: PropTypes.string,
  sortOrder: PropTypes.string,
  conditionOption: PropTypes.string,
  categoryIds: PropTypes.string,
  minHeight: PropTypes.string,
  autoplay: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
  fade: PropTypes.bool,
  infinite: PropTypes.bool,
  showArrows: PropTypes.bool,
  showDots: PropTypes.bool,
  textAlign: PropTypes.string,
  border: PropTypes.string,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.string,
  borderRadius: PropTypes.string,
  marginTop: PropTypes.string,
  marginRight: PropTypes.string,
  marginBottom: PropTypes.string,
  marginLeft: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingBottom: PropTypes.string,
  paddingLeft: PropTypes.string,
  cssClasses: PropTypes.arrayOf(PropTypes.string),
};

export default NewProductSlider;